import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import api from '../utils/api';
import { toast } from '../hooks/use-toast';
import { 
  GraduationCap, FileText, CreditCard, ClipboardList, Calendar,
  MessageCircle, Users, CheckCircle, ExternalLink, ChevronRight,
  Loader2, Send, Bot, User, ArrowRight, BookOpen, Plane,
  Building, DollarSign, Clock, AlertTriangle, Sparkles
} from 'lucide-react';

const F1VisaGuide = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const steps = [
    {
      id: 1,
      title: 'Research & Apply to Universities',
      icon: GraduationCap,
      color: 'emerald',
      timeline: '12-18 months before',
      description: 'Research universities, prepare applications, and get accepted.',
      details: [
        'Research universities and programs that match your goals',
        'Check admission requirements (GPA, standardized tests, English proficiency)',
        'Prepare required documents: transcripts, recommendation letters, essays',
        'Take required tests: TOEFL/IELTS, SAT, GRE/GMAT (if required)',
        'Submit applications before deadlines',
        'Apply for scholarships and financial aid'
      ],
      links: [
        { title: 'College Board - Find Colleges', url: 'https://www.collegeboard.org/' },
        { title: 'US News College Rankings', url: 'https://www.usnews.com/best-colleges' },
        { title: 'SAT Registration', url: 'https://satsuite.collegeboard.org/sat/registration' },
        { title: 'TOEFL Registration', url: 'https://www.ets.org/toefl' },
        { title: 'GRE Registration', url: 'https://www.ets.org/gre' }
      ],
      tips: [
        'Apply to multiple universities (safety, match, and reach schools)',
        'Start early - applications can take months to complete',
        'Request recommendation letters at least 2 months in advance'
      ]
    },
    {
      id: 2,
      title: 'Receive Acceptance & I-20',
      icon: FileText,
      color: 'blue',
      timeline: '6-8 months before',
      description: 'Get your acceptance letter and I-20 form from the university.',
      details: [
        'Accept admission offer from your chosen university',
        'Submit financial documents proving you can afford education',
        'Pay enrollment deposit (if required)',
        'Receive Form I-20 from the university',
        'Review I-20 carefully for accuracy',
        'Sign the I-20 form'
      ],
      links: [
        { title: 'What is Form I-20?', url: 'https://studyinthestates.dhs.gov/students/prepare/students-form-i-20' },
        { title: 'SEVP School Search', url: 'https://studyinthestates.dhs.gov/school-search' }
      ],
      tips: [
        'Keep multiple copies of your I-20',
        'The I-20 contains your SEVIS ID (starts with N)',
        'Your I-20 program dates determine your F-1 status period'
      ]
    },
    {
      id: 3,
      title: 'Pay SEVIS I-901 Fee',
      icon: CreditCard,
      color: 'purple',
      timeline: '3-4 months before',
      description: 'Pay the mandatory SEVIS fee before your visa interview.',
      details: [
        'Go to FMJfee.com to pay the I-901 SEVIS fee',
        'You need your SEVIS ID from your I-20',
        'Fee amount: $350 for F-1 students (may change as per USCIS updates)',
        'Payment methods: credit card, debit card, or check',
        'Save and print your payment confirmation receipt',
        'Payment must be made at least 3 days before your interview'
      ],
      links: [
        { title: 'Pay SEVIS Fee (Official)', url: 'https://www.fmjfee.com/' },
        { title: 'SEVIS Fee FAQ', url: 'https://studyinthestates.dhs.gov/sevis-i-901-fee' }
      ],
      tips: [
        'Keep the payment receipt - you need it for your visa interview',
        'The fee is nonrefundable',
        'Pay at least 3 business days before your interview'
      ]
    },
    {
      id: 4,
      title: 'Complete DS-160 Application',
      icon: ClipboardList,
      color: 'orange',
      timeline: '2-3 months before',
      description: 'Fill out the online nonimmigrant visa application form.',
      details: [
        'Go to ceac.state.gov to access DS-160',
        'Select your interview location (country/city)',
        'Fill out all sections: personal info, travel, education, work history',
        'Upload a passport-style photo meeting requirements',
        'Review all information carefully before submission',
        'Submit and save your confirmation page with barcode'
      ],
      links: [
        { title: 'DS-160 Online Application', url: 'https://ceac.state.gov/genniv/' },
        { title: 'DS-160 Instructions', url: 'https://travel.state.gov/content/travel/en/us-visas/visa-information-resources/forms/ds-160-online-nonimmigrant-visa-application.html' },
        { title: 'Photo Requirements', url: 'https://travel.state.gov/content/travel/en/us-visas/visa-information-resources/photos.html' }
      ],
      tips: [
        'Save your Application ID - you can resume if needed',
        'The form times out after 20 minutes of inactivity',
        'Be 100% truthful - inconsistencies can lead to denial',
        'Use our AI Interview Prep to practice for your interview'
      ]
    },
    {
      id: 5,
      title: 'Schedule Visa Interview',
      icon: Calendar,
      color: 'pink',
      timeline: '1-2 months before',
      description: 'Book your visa interview appointment at the US Embassy/Consulate.',
      details: [
        'Create account on your country\'s visa appointment website',
        'Pay the visa application fee (MRV fee) - $185 for F-1',
        'Schedule your visa interview appointment',
        'Schedule biometrics appointment (if required)',
        'Note: Interview wait times vary by location'
      ],
      links: [
        { title: 'US Visa Appointment (India)', url: 'https://www.ustraveldocs.com/in/' },
        { title: 'US Visa Appointment (Pakistan)', url: 'https://www.ustraveldocs.com/pk/' },
        { title: 'US Visa Appointment (Bangladesh)', url: 'https://www.ustraveldocs.com/bd/' },
        { title: 'US Visa Appointment (Sri Lanka)', url: 'https://www.ustraveldocs.com/lk/' },
        { title: 'Find Your Country\'s Site', url: 'https://www.usembassy.gov/' },
        { title: 'Visa Wait Times', url: 'https://travel.state.gov/content/travel/en/us-visas/visa-information-resources/wait-times.html' }
      ],
      tips: [
        'Book early - popular times fill up quickly',
        'Check multiple cities for earlier appointments',
        'Keep your MRV fee receipt'
      ]
    },
    {
      id: 6,
      title: 'Attend Visa Interview',
      icon: Users,
      color: 'teal',
      timeline: 'Interview day',
      description: 'Attend your F-1 visa interview at the US Embassy/Consulate.',
      details: [
        'Arrive early (at least 30 minutes before)',
        'Bring all required documents (see checklist below)',
        'Answer questions honestly and confidently',
        'Explain your study plans and ties to home country',
        'Interview usually lasts 3-5 minutes'
      ],
      documents: [
        'Valid passport (valid 6+ months beyond intended stay)',
        'DS-160 confirmation page with barcode',
        'I-20 form (signed)',
        'SEVIS I-901 fee receipt',
        'Visa appointment confirmation',
        'Passport-sized photos (2x2 inches)',
        'Financial documents (bank statements, sponsor letter)',
        'Academic documents (transcripts, diplomas, test scores)',
        'Admission letter from university'
      ],
      links: [
        { title: 'Interview Tips', url: 'https://travel.state.gov/content/travel/en/us-visas/visa-information-resources/visa-denials.html' },
        { title: 'Practice with AI Interview', url: '/interview/realtime' }
      ],
      tips: [
        'Practice common interview questions',
        'Dress professionally',
        'Bring original documents + copies',
        'Stay calm and be concise in your answers'
      ]
    },
    {
      id: 7,
      title: 'Receive Visa & Prepare to Travel',
      icon: Plane,
      color: 'cyan',
      timeline: 'After approval',
      description: 'Get your visa and prepare for your journey to the US.',
      details: [
        'If approved, your passport will be returned with visa stamp',
        'Processing typically takes 3-5 business days',
        'Review visa for accuracy (name, dates, visa type)',
        'Book your flight (can enter US up to 30 days before program start)',
        'Arrange housing and prepare for arrival',
        'Attend any pre-departure orientation from your university'
      ],
      links: [
        { title: 'Preparing for Arrival', url: 'https://studyinthestates.dhs.gov/students/prepare' },
        { title: 'Port of Entry Tips', url: 'https://studyinthestates.dhs.gov/students/arrive' }
      ],
      tips: [
        'Keep all documents in your carry-on luggage',
        'Have your university\'s international office contact info handy',
        'Report to your school within 30 days of program start'
      ]
    }
  ];

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);
    
    try {
      const response = await api.post('/visa-guide/ask', { question: userMessage });
      
      setChatMessages(prev => [...prev, { role: 'assistant', content: response.data.answer }]);
    } catch (error) {
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'I apologize, but I encountered an error. Please try again or contact support for help with your visa questions.' 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-emerald-100 text-emerald-700 text-sm px-4 py-1">
            Complete F-1 Visa Guide
          </Badge>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Your Path to Studying in the USA
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Follow this step-by-step guide to successfully obtain your F-1 student visa
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Steps */}
          <div className="lg:col-span-2 space-y-6">
            {/* Timeline Progress */}
            <Card className="bg-white/80 backdrop-blur">
              <CardContent className="py-4">
                <div className="flex items-center justify-between overflow-x-auto pb-2">
                  {steps.map((step, index) => (
                    <button
                      key={step.id}
                      onClick={() => setActiveStep(index)}
                      className={`flex flex-col items-center min-w-[80px] transition-all ${
                        activeStep === index ? 'scale-110' : 'opacity-60 hover:opacity-100'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
                        activeStep === index 
                          ? `bg-${step.color}-500 text-white` 
                          : 'bg-gray-200 text-gray-500'
                      }`} style={{ backgroundColor: activeStep === index ? `var(--${step.color}-500, #10b981)` : undefined }}>
                        <step.icon className="h-5 w-5" />
                      </div>
                      <span className="text-xs font-medium text-center">Step {step.id}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Active Step Detail */}
            <Card className="bg-white shadow-lg">
              <CardHeader className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-t-lg">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                    {React.createElement(steps[activeStep].icon, { className: 'h-8 w-8' })}
                  </div>
                  <div>
                    <Badge className="bg-white/20 text-white mb-2">
                      {steps[activeStep].timeline}
                    </Badge>
                    <CardTitle className="text-2xl">
                      Step {steps[activeStep].id}: {steps[activeStep].title}
                    </CardTitle>
                    <CardDescription className="text-white/90">
                      {steps[activeStep].description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                {/* What to Do */}
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-emerald-500" />
                    What to Do
                  </h3>
                  <ul className="space-y-2">
                    {steps[activeStep].details.map((detail, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-700">
                        <ChevronRight className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Documents (if any) */}
                {steps[activeStep].documents && (
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-500" />
                      Required Documents
                    </h3>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <ul className="grid md:grid-cols-2 gap-2">
                        {steps[activeStep].documents.map((doc, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-gray-700 text-sm">
                            <CheckCircle className="h-4 w-4 text-blue-500 flex-shrink-0" />
                            {doc}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Helpful Links */}
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <ExternalLink className="h-5 w-5 text-purple-500" />
                    Helpful Links
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {steps[activeStep].links.map((link, idx) => (
                      <a
                        key={idx}
                        href={link.url}
                        target={link.url.startsWith('/') ? '_self' : '_blank'}
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors text-sm"
                      >
                        {link.title}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ))}
                  </div>
                </div>

                {/* Tips */}
                {steps[activeStep].tips && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h3 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5" />
                      Pro Tips
                    </h3>
                    <ul className="space-y-1">
                      {steps[activeStep].tips.map((tip, idx) => (
                        <li key={idx} className="text-amber-700 text-sm flex items-start gap-2">
                          <Sparkles className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex justify-between pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                    disabled={activeStep === 0}
                  >
                    ← Previous Step
                  </Button>
                  <Button
                    onClick={() => setActiveStep(Math.min(steps.length - 1, activeStep + 1))}
                    disabled={activeStep === steps.length - 1}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    Next Step →
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - AI Chat */}
          <div className="lg:sticky lg:top-20 h-fit">
            <Card className="bg-white shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <Bot className="h-6 w-6" />
                  Visa Assistant AI
                </CardTitle>
                <CardDescription className="text-white/90">
                  Ask any questions about the F-1 visa process
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {/* Chat Messages */}
                <div className="h-80 overflow-y-auto p-4 space-y-4">
                  {chatMessages.length === 0 && (
                    <div className="text-center text-gray-500 py-8">
                      <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Ask me anything about the F-1 visa process!</p>
                      <div className="mt-4 space-y-2">
                        <button
                          onClick={() => setChatInput('What documents do I need for my visa interview?')}
                          className="block w-full text-left text-sm bg-gray-100 hover:bg-gray-200 p-2 rounded"
                        >
                          "What documents do I need for my visa interview?"
                        </button>
                        <button
                          onClick={() => setChatInput('How long does it take to get an F-1 visa?')}
                          className="block w-full text-left text-sm bg-gray-100 hover:bg-gray-200 p-2 rounded"
                        >
                          "How long does it take to get an F-1 visa?"
                        </button>
                        <button
                          onClick={() => setChatInput('Can I work while on F-1 visa?')}
                          className="block w-full text-left text-sm bg-gray-100 hover:bg-gray-200 p-2 rounded"
                        >
                          "Can I work while on F-1 visa?"
                        </button>
                      </div>
                    </div>
                  )}
                  {chatMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Bot className="h-5 w-5 text-blue-600" />
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] p-3 rounded-lg text-sm ${
                          msg.role === 'user'
                            ? 'bg-emerald-500 text-white rounded-br-none'
                            : 'bg-gray-100 text-gray-800 rounded-bl-none'
                        }`}
                      >
                        {msg.content}
                      </div>
                      {msg.role === 'user' && (
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="h-5 w-5 text-emerald-600" />
                        </div>
                      )}
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex gap-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <Bot className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="bg-gray-100 p-3 rounded-lg">
                        <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Chat Input */}
                <div className="border-t p-4">
                  <div className="flex gap-2">
                    <Input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask about F-1 visa process..."
                      disabled={chatLoading}
                    />
                    <Button
                      onClick={sendChatMessage}
                      disabled={chatLoading || !chatInput.trim()}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card className="mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <a href="/interview/realtime" className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors">
                  <Users className="h-5 w-5 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700">Practice Visa Interview</span>
                  <ArrowRight className="h-4 w-4 ml-auto text-emerald-600" />
                </a>
                <a href="/colleges" className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                  <Building className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-700">Search Colleges</span>
                  <ArrowRight className="h-4 w-4 ml-auto text-blue-600" />
                </a>
                <a href="/scholarships" className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                  <DollarSign className="h-5 w-5 text-purple-600" />
                  <span className="text-sm font-medium text-purple-700">Find Scholarships</span>
                  <ArrowRight className="h-4 w-4 ml-auto text-purple-600" />
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default F1VisaGuide;
